import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | undefined} */
  #observer;
  #loaded = false;

  // Deimantės mezginiai: video kraunamas tik tada, kai komponentas yra (beveik) ekrane ir matomas,
  // išslinkus — sustabdomas. Anksčiau visi puslapio video buvo kraunami iš karto (~60 MB telefone).
  connectedCallback() {
    super.connectedCallback();
    if (!('IntersectionObserver' in window)) return this.#load();

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const { videoElement } = this.refs;
          if (entry.isIntersecting && this.offsetWidth > 0) {
            this.#load();
            videoElement.play()?.catch(() => {});
          } else if (this.#loaded && !videoElement.paused) {
            videoElement.pause();
          }
        }
      },
      { rootMargin: '250px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  #load() {
    if (this.#loaded) return;
    this.#loaded = true;
    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
